import { seasonUnlockInputSchema } from "@frontier/content/season";
import type { z } from "zod";
import type { Profile } from "@frontier/protocol/contracts";
import type { AuthStorage } from "./auth-storage";
import { api, ApiError, requestId } from "./session-api";

export type SeasonUnlockIntent = z.infer<typeof seasonUnlockInputSchema>;
export interface SeasonUnlockStorage extends AuthStorage {
  keys(): readonly string[];
}
type Quote = Omit<SeasonUnlockIntent, "requestId">;
type Outcome =
  | { status: "confirmed" }
  | { status: "superseded" }
  | { status: "uncertain"; message: string }
  | { status: "rejected"; message: string; priceChanged: boolean };
const storageMessage = "Для покупки сезона разреши сохранение данных игры.";
export function browserSeasonUnlockStorage(): SeasonUnlockStorage {
  return {
    keys() {
      try {
        return Object.keys(window.localStorage);
      } catch {
        throw new Error(storageMessage);
      }
    },
    getItem(key) {
      try {
        return window.localStorage.getItem(key);
      } catch {
        throw new Error(storageMessage);
      }
    },
    setItem(key, value) {
      try {
        window.localStorage.setItem(key, value);
        if (window.localStorage.getItem(key) !== value)
          throw new Error(storageMessage);
      } catch {
        throw new Error(storageMessage);
      }
    },
    removeItem(key) {
      try {
        window.localStorage.removeItem(key);
      } catch {
        throw new Error(storageMessage);
      }
    },
  };
}
function sameIntent(
  left: SeasonUnlockIntent,
  right: SeasonUnlockIntent,
): boolean {
  return (
    left.requestId === right.requestId &&
    left.seasonId === right.seasonId &&
    left.priceVersion === right.priceVersion
  );
}
export class SeasonUnlockOperations {
  constructor(
    private readonly storage: SeasonUnlockStorage,
    private readonly createId: () => string = requestId,
  ) {}
  pending(playerId: string): SeasonUnlockIntent | null {
    return this.records(playerId)[0]?.intent ?? null;
  }
  begin(playerId: string, quote: Quote): SeasonUnlockIntent {
    const prior = this.pending(playerId);
    if (prior) return prior;
    const intent = seasonUnlockInputSchema.parse({
      ...quote,
      requestId: this.createId(),
    });
    // Independent keys preserve both identities when two tabs begin between each other's read and write.
    this.storage.setItem(
      `${this.key(playerId)}.${intent.requestId}`,
      JSON.stringify(intent),
    );
    if (!this.owns(playerId, intent)) throw new Error(storageMessage);
    return intent;
  }
  async execute(
    playerId: string,
    token: string,
    intent: SeasonUnlockIntent,
    current: () => boolean,
    client: Pick<typeof api, "unlockSeason"> = api,
  ): Promise<Outcome> {
    if (!current() || !this.owns(playerId, intent))
      return { status: "superseded" };
    try {
      const profile: Profile = await client.unlockSeason(token, intent);
      if (!current() || !this.owns(playerId, intent))
        return { status: "superseded" };
      if (profile.playerId !== playerId)
        throw new Error(
          "Не удалось подтвердить покупку сезона. Проверь операцию.",
        );
      const selected = this.isSelected(playerId, intent);
      this.clear(playerId, intent);
      return { status: selected ? "confirmed" : "superseded" };
    } catch (error) {
      if (!current() || !this.owns(playerId, intent))
        return { status: "superseded" };
      const selected = this.isSelected(playerId, intent);
      const rejected =
        error instanceof ApiError &&
        error.status >= 400 &&
        error.status < 500 &&
        ![401, 408, 426, 429].includes(error.status) &&
        error.code !== "CLIENT_UPDATE_REQUIRED";
      if (!rejected)
        return selected
          ? {
              status: "uncertain",
              message: "Результат покупки неизвестен. Проверь операцию.",
            }
          : { status: "superseded" };
      this.clear(playerId, intent);
      return selected
        ? {
            status: "rejected",
            message: error.message,
            priceChanged: error.code === "SEASON_PRICE_CHANGED",
          }
        : { status: "superseded" };
    }
  }
  private isSelected(playerId: string, intent: SeasonUnlockIntent): boolean {
    const selected = this.pending(playerId);
    return selected !== null && sameIntent(selected, intent);
  }
  private owns(playerId: string, intent: SeasonUnlockIntent): boolean {
    return this.records(playerId).some((record) =>
      sameIntent(record.intent, intent),
    );
  }
  private clear(playerId: string, intent: SeasonUnlockIntent): void {
    for (const record of this.records(playerId))
      if (sameIntent(record.intent, intent)) {
        if (this.storage.getItem(record.key) === record.raw)
          this.storage.removeItem(record.key);
      }
  }
  private records(
    playerId: string,
  ): { key: string; raw: string; intent: SeasonUnlockIntent }[] {
    const base = this.key(playerId),
      keys = [
        base,
        ...this.storage
          .keys()
          .filter((key) => key.startsWith(`${base}.`))
          .toSorted(),
      ];
    const records: { key: string; raw: string; intent: SeasonUnlockIntent }[] =
      [];
    for (const key of keys) {
      const raw = this.storage.getItem(key);
      if (!raw) continue;
      try {
        records.push({
          key,
          raw,
          intent: seasonUnlockInputSchema.parse(JSON.parse(raw)),
        });
      } catch {
        console.warn("season-unlock-intent-invalid", { playerId });
        throw new Error(
          "Не удалось восстановить покупку сезона. Сообщи об ошибке через помощь.",
        );
      }
    }
    return records;
  }
  private key(playerId: string): string {
    return `frontier.seasonUnlock.${playerId}`;
  }
}
